import Modal from '../components/Modal';
import Dropdown from '../components/Dropdown';
import Input from '../components/Input';
import LabelWithTooltip from '../components/LabelWithTooltip';
import React, { useEffect, useMemo } from 'react';
import AlertAccent from '../components/AlertErrorBox';
import ConfirmAlert from '../components/ConfirmAlert';
import { checkControllerSerialNumber } from '../utils/generateSerialNumber';
import ListChannels from './ListChannels';
import TitleWithIcon from '../components/TitleWithIcon';
import { useChannelEdit } from './hooks/useChannelEdit';

// вынесено из controllerEditor без рефакторинга для улучшения читаемости
const ChannelSettings = ({ devices, deviceToEdit, deviceInputs, initialConfiguration, setDeviceInputs }) => {
  const [inputToDelete, setInputToDelete] = React.useState();
  const [error, setError] = React.useState([]);
  const [showAlert, setShowAlert] = React.useState(false);
  const devicesWithoutControllers = devices?.filter((item) => item?.type?.key !== 'controller');
  const initialInput =
    devicesWithoutControllers?.length > 0
      ? {
          ...devicesWithoutControllers[0],
          ...{ input_number: 1, device_id: devicesWithoutControllers?.[0]?.id, modifiers: { multiplier: 10 } },
        }
      : null;
  const [inputToAdd, setInputToAdd] = React.useState(initialInput);
  const isCounterForAddDevice = inputToAdd?.type?.key === 'counter';

  const { isEditMode, inputToEdit, showModal, handleOpenModal, handleCloseModal } = useChannelEdit(deviceInputs, initialInput, setInputToAdd, setError);

  const isStandardInputNumber = checkControllerSerialNumber(deviceToEdit?.serial_number);

  const optionsChannel = useMemo(() => {
    let initialChannels = initialConfiguration?.channels;
    if (isStandardInputNumber) {
      initialChannels = initialChannels.slice(0, 3);
    }

    if (isEditMode) {
      return initialChannels || [];
    }

    const omitOptions = deviceInputs?.map((item) => item?.input_number) ?? [];
    const omitSet = new Set(omitOptions);
    return initialChannels?.filter((ch) => !omitSet.has(ch.val)) || [];
  }, [deviceInputs, initialConfiguration.channels, isStandardInputNumber, isEditMode]);

  useEffect(() => {
    if (showModal && !isEditMode && optionsChannel?.[0]?.val) {
      setInputToAdd((prev) => ({ ...prev, input_number: optionsChannel[0].val }));
    }
  }, [showModal, optionsChannel, isEditMode]);

  const setDevData = (e) => {
    let devToAdd = {};
    devices.map((dev) => {
      if (dev.id === e) devToAdd = dev;
    });

    devToAdd.device_id = devToAdd.id;

    setInputToAdd({ ...inputToAdd, ...devToAdd });
  };

  const deleteInput = () => {
    setDeviceInputs(deviceInputs.filter((a) => a.input_number !== inputToDelete));
    setShowAlert(false);
  };

  const onDevInputAdd = () => {
    setError([]);
    const inputsToCheck = isEditMode ? deviceInputs.filter((input) => input.input_number !== inputToEdit?.input_number) : deviceInputs;
    const isDuplicateChannel = inputsToCheck.some((input) => input.input_number === inputToAdd.input_number);
    const isDuplicateDevice = inputsToCheck.some((input) => input.device_id === inputToAdd.device_id);

    const isEmptyAdd = !(inputToAdd.modifiers?.add || inputToAdd.modifiers?.add === 0);
    const newErrors = [];
    if (isEmptyAdd && isCounterForAddDevice) {
      newErrors.push({ error: 'Введите начальные показания при установке' });
    }

    if (isDuplicateChannel) {
      newErrors.push({ error: 'Выберите свободный канал' });
    }

    if (isDuplicateDevice) {
      newErrors.push({ error: 'Выберите свободное устройство' });
    }

    if (newErrors.length > 0) {
      setError(newErrors);
    } else {
      const updatedInput = {
        input_number: inputToAdd.input_number,
        device_id: inputToAdd.device_id,
        modifiers: {
          multiplier: isCounterForAddDevice ? inputToAdd.modifiers?.multiplier : 1,
          add: isCounterForAddDevice ? inputToAdd.modifiers?.add : 0,
        },
        serial_number: inputToAdd.serial_number,
        type: inputToAdd.type,
      };

      if (isEditMode) {
        setDeviceInputs((prev) => prev.map((input) => (input.input_number === inputToEdit.input_number ? updatedInput : input)));
      } else {
        setDeviceInputs((prev) => [...prev, updatedInput]);
      }

      handleCloseModal();
    }
  };

  return (
    <>
      {devicesWithoutControllers?.length > 0 ? (
        <>
          <TitleWithIcon
            title={'Настройка каналов'}
            handler={() => handleOpenModal(false)}
            buttonStyle={'p-1'}
            titleStyle={'text-xl'}
            containerStyle={'justify-between gap-2 px-4'}
          />

          <Modal
            saveCb={onDevInputAdd}
            error={error}
            title={isEditMode ? 'Редактирование канала' : 'Настройка каналов'}
            showModal={showModal}
            setShowModal={handleCloseModal}
            classWidth="relative w-11/12 lg:w-1/2 my-6 mx-2">
            <Dropdown
              className={'mb-4'}
              value={inputToAdd?.input_number}
              label={'Выбрать канал'}
              emptyoption={'Нет доступных каналов'}
              options={optionsChannel}
              inputCb={(e) =>
                setInputToAdd({
                  ...inputToAdd,
                  input_number: parseInt(e.target.value),
                })
              }
              id="ch"
              valkey="val"
              optkey="opt"
            />

            <Dropdown
              className={'mb-4'}
              value={inputToAdd?.device_id}
              label={'Выбрать устройство'}
              options={devicesWithoutControllers}
              inputCb={(e) => setDevData(parseInt(e.target.value))}
              id="devs"
              valkey="id"
              optkey="serial_number"
            />

            {isCounterForAddDevice && (
              <>
                <Input
                  className="w-full mb-4"
                  value={inputToAdd?.modifiers?.add}
                  inputCb={(e) => {
                    const val = e.target.value;
                    setInputToAdd({
                      ...inputToAdd,
                      modifiers: {
                        ...inputToAdd.modifiers,
                        add: val === '' ? '' : +val,
                      },
                    });
                  }}
                  label={
                    <LabelWithTooltip
                      text={'Показания при установке в литрах'}
                      tooltipText={'4,736 куб.метр = 4736 литров'}
                      tooltipImage={'/images/meter.png'}
                    />
                  }
                  id="val"
                  type="number"
                  placeholder={'Введите показания при установке'}
                  onWheel={(e) => e.currentTarget.blur()}
                />
                <Dropdown
                  className={'mb-4'}
                  value={inputToAdd?.modifiers?.multiplier}
                  label={'Коэффициент расхода воды'}
                  options={initialConfiguration.multiplier}
                  inputCb={(e) => setInputToAdd({ ...inputToAdd, modifiers: { ...inputToAdd.modifiers, multiplier: parseInt(e.target.value) } })}
                  id="multiplier"
                  valkey="val"
                  optkey="opt"
                />
              </>
            )}
          </Modal>
          <ListChannels
            data={deviceInputs}
            editHandler={(id) => {
              handleOpenModal(true, id);
            }}
            deleteHandler={(id) => {
              setInputToDelete(id);
              setShowAlert(true);
            }}
          />
        </>
      ) : null}
      <ConfirmAlert showAlert={showAlert} setShowAlert={setShowAlert} delCb={deleteInput} title="Удаление канала">
        <AlertAccent text="Вы действительно хотите удалить канал?" showAlert={true}>
          <p>Номер канала: {inputToDelete}</p>
        </AlertAccent>
      </ConfirmAlert>
    </>
  );
};

export default ChannelSettings;
